import type { JSX } from "react";

/**
 * PH-5 generating-state skeleton (openspec/meeting-capture.md §12, uiux §5).
 * Shown while the map-reduce summary is being produced. HONEST by design: the
 * summarizer emits all five parts at once (no per-section progress), so this does
 * NOT fake per-section "done" states — every section is a uniform shimmer with its
 * real fixed label, staggered-fading-in to read as "structure forming". The
 * elapsed counter is real; the narration line is static and content-free.
 */

// The five parts, in the same order + labels the real MeetingSummaryBlock uses.
const SECTIONS: { label: string; bars: number }[] = [
  { label: "摘要", bars: 2 },
  { label: "For you", bars: 2 },
  { label: "Decisions", bars: 3 },
  { label: "Action items · others", bars: 1 },
  { label: "Risks & open questions", bars: 1 }
];

export interface MeetingGeneratingSkeletonProps {
  /** Seconds since Stop was pressed (real elapsed, not a fabricated ETA). */
  elapsedSec: number;
}

export function MeetingGeneratingSkeleton({ elapsedSec }: MeetingGeneratingSkeletonProps): JSX.Element {
  return (
    <div className="mv-gen" role="status" aria-live="polite">
      <div className="mv-gen-chip">
        <span className="mv-gen-ring" aria-hidden="true" />
        <span className="mv-gen-label">生成纪要中</span>
        <span className="mv-gen-elapsed">{elapsedSec}s</span>
      </div>
      <div className="mv-gen-narration">
        <span className="mv-gen-av" aria-hidden="true">✦</span>
        小环正在读这场会，起草五段纪要…
      </div>
      <div className="mv-gen-sections">
        {SECTIONS.map((s, i) => (
          <div key={s.label} className="mv-gen-sec" style={{ animationDelay: `${i * 0.12}s` }}>
            <div className="mv-gen-sec-head">{s.label}</div>
            {Array.from({ length: s.bars }, (_, b) => (
              <div key={b} className="mv-gen-skel" style={{ width: `${[92, 60, 78][b % 3]}%` }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
